import React, { useEffect, useRef, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  Mail, Instagram, Linkedin, Send, MapPin, ArrowRight, Menu, X,
  BedDouble, Workflow, BarChart3, ShieldCheck, Clock, Building2, Check,
  Plus, AlertCircle,
} from 'lucide-react';
import Reveal from './components/Reveal';
import { getDirection } from './i18n';

const GOOGLE_SCRIPT_URL =
  'https://script.google.com/macros/s/AKfycbzTtGL393uMJTS46_oX2WlPnRIomNw12IFF0Bhj9rZitSms6VDSc89zD_B7tfYwAdxbew/exec';
const PILOT_URL = 'https://careascension-web.vercel.app';
const INSTAGRAM_URL = 'https://www.instagram.com/care.ascension/';
const LINKEDIN_URL = 'https://www.linkedin.com/company/careascension/';
const CONTACT_EMAIL = 'operations@careascension.com';

const NAV_ITEMS = ['problem', 'product', 'about', 'partners', 'faq'];
const FEATURE_ICONS = [BedDouble, Workflow, BarChart3, Clock, ShieldCheck, Building2];

// Point-in-time homelessness counts; x/y are plot coordinates in the 400×240 viewBox.
const CHART_POINTS = [
  { year: '2017', count: 8947, x: 80, y: 140 },
  { year: '2019', count: 10007, x: 180, y: 110 },
  { year: '2022', count: 13553, x: 280, y: 65 },
  { year: '2023', count: 14237, x: 380, y: 50 },
];

const ABOUT_PHOTOS = [
  { src: '/assets/about-cityhall.jpg', className: 'row-span-2 aspect-[3/4]' },
  { src: '/assets/about-outreach.jpg', className: 'aspect-square' },
  { src: '/assets/about-team.jpg', className: 'row-span-2 aspect-[4/3]' },
];

const iconProps = { 'aria-hidden': 'true', focusable: 'false' };

/* -------------------------------------------------------------------------- */
/*  Reusable building blocks                                                  */
/* -------------------------------------------------------------------------- */

function Section({ id, labelledBy, className = '', children }) {
  return (
    <section
      id={id}
      aria-labelledby={labelledBy}
      className={`scroll-mt-24 px-6 py-24 md:py-32 ${className}`}
    >
      <div className="mx-auto max-w-6xl">{children}</div>
    </section>
  );
}

function SectionIntro({ headingId, eyebrow, heading, children, tone = 'light' }) {
  const headingColor = tone === 'dark' ? 'text-white' : 'text-slate-900';
  const leadColor = tone === 'dark' ? 'text-slate-300' : 'text-slate-600';
  return (
    <div className="max-w-2xl">
      <p className="mb-4 text-sm font-semibold uppercase tracking-[0.14em] text-primary">
        {eyebrow}
      </p>
      <h2
        id={headingId}
        className={`font-heading text-3xl font-bold tracking-tight md:text-4xl ${headingColor}`}
      >
        {heading}
      </h2>
      {children ? (
        <p className={`mt-5 text-lg leading-relaxed ${leadColor}`}>{children}</p>
      ) : null}
    </div>
  );
}

function Photo({ src, alt, className = '' }) {
  const [failed, setFailed] = useState(false);
  return (
    <figure
      className={`group relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 ${className}`}
    >
      {failed ? (
        <div
          role="img"
          aria-label={alt}
          className="flex h-full w-full items-center justify-center p-4 text-center text-xs font-medium text-slate-500"
        >
          {alt}
        </div>
      ) : (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
          className="h-full w-full object-cover transition-transform duration-500 ease-out motion-safe:group-hover:scale-[1.04]"
        />
      )}
    </figure>
  );
}

function FaqItem({ question, answer }) {
  return (
    <details className="group py-5">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 rounded-md font-heading text-base font-semibold text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-4">
        {question}
        <Plus
          {...iconProps}
          size={18}
          className="shrink-0 text-slate-500 transition-transform group-open:rotate-45"
        />
      </summary>
      <p className="mt-3 text-sm leading-relaxed text-slate-600">{answer}</p>
    </details>
  );
}

/* -------------------------------------------------------------------------- */
/*  Contact form                                                             */
/* -------------------------------------------------------------------------- */

function ContactForm() {
  const { t } = useTranslation();
  const [values, setValues] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const feedbackRef = useRef(null);

  useEffect(() => {
    if ((status === 'success' || status === 'error') && feedbackRef.current) {
      feedbackRef.current.focus();
    }
  }, [status]);

  const handleChange = (e) =>
    setValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...values, timestamp: new Date().toISOString() }),
      });
      setStatus('success');
      setValues({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Contact form submission failed:', error);
      setStatus('error');
    }
  };

  const fieldClass =
    'w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-500 transition focus:border-primary focus-visible:outline-none focus:ring-2 focus:ring-primary/30';

  if (status === 'success') {
    return (
      <div
        ref={feedbackRef}
        tabIndex={-1}
        role="status"
        className="flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white p-8 py-14 text-center shadow-soft focus-visible:outline-none"
      >
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
          <Check {...iconProps} size={24} />
        </span>
        <h3 className="mt-4 font-heading text-lg font-bold text-slate-900">
          {t('contact.form.successTitle')}
        </h3>
        <p className="mt-2 text-sm text-slate-600">{t('contact.form.successBody')}</p>
      </div>
    );
  }

  const sending = status === 'sending';

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="space-y-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-soft md:p-8"
    >
      {status === 'error' && (
        <p
          ref={feedbackRef}
          tabIndex={-1}
          role="alert"
          className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-800 focus-visible:outline-none"
        >
          <AlertCircle {...iconProps} size={16} className="mt-0.5 shrink-0" />
          <span>
            <strong className="font-semibold">{t('contact.form.errorTitle')}.</strong>{' '}
            {t('contact.form.errorBody')}
          </span>
        </p>
      )}

      {[
        { id: 'name', type: 'text', autoComplete: 'name' },
        { id: 'email', type: 'email', autoComplete: 'email' },
      ].map((field) => (
        <div key={field.id}>
          <label
            htmlFor={`contact-${field.id}`}
            className="mb-1.5 block text-sm font-medium text-slate-700"
          >
            {t(`contact.form.${field.id}`)}{' '}
            <span className="font-normal text-slate-500">
              ({t('contact.form.required')})
            </span>
          </label>
          <input
            id={`contact-${field.id}`}
            name={field.id}
            type={field.type}
            autoComplete={field.autoComplete}
            value={values[field.id]}
            onChange={handleChange}
            required
            aria-required="true"
            placeholder={t(`contact.form.${field.id}Placeholder`)}
            className={fieldClass}
          />
        </div>
      ))}

      <div>
        <label
          htmlFor="contact-message"
          className="mb-1.5 block text-sm font-medium text-slate-700"
        >
          {t('contact.form.message')}{' '}
          <span className="font-normal text-slate-500">
            ({t('contact.form.required')})
          </span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          rows="4"
          value={values.message}
          onChange={handleChange}
          required
          aria-required="true"
          placeholder={t('contact.form.messagePlaceholder')}
          className={`${fieldClass} resize-none`}
        />
      </div>

      <button
        type="submit"
        disabled={sending}
        aria-disabled={sending}
        className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 px-6 py-3 text-sm font-semibold text-white shadow-soft transition-colors hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 disabled:opacity-60"
      >
        <Send {...iconProps} size={16} />
        {sending ? t('contact.form.submitting') : t('contact.form.submit')}
      </button>
    </form>
  );
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                     */
/* -------------------------------------------------------------------------- */

export default function LandingPage() {
  const { t, i18n } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const language = i18n.resolvedLanguage || i18n.language || 'en';
  const numberFormat = new Intl.NumberFormat(language);

  // Keep <html lang/dir>, the tab title, and the meta description in sync.
  useEffect(() => {
    const lang = language.split('-')[0];
    document.documentElement.lang = lang;
    document.documentElement.dir = getDirection(language);
    document.title = t('meta.title');
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', t('meta.description'));
  }, [language, t]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const onKeyDown = (e) => e.key === 'Escape' && setMobileMenuOpen(false);
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mobileMenuOpen]);

  const navLinkClass =
    'relative rounded-md text-sm font-medium text-slate-600 transition-colors hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-4 ' +
    'after:absolute after:-bottom-1 after:left-0 after:h-0.5 after:w-full after:origin-left after:scale-x-0 after:rounded-full after:bg-primary after:transition-transform after:duration-300 hover:after:scale-x-100';

  const asArray = (key) => {
    const value = t(key, { returnObjects: true });
    return Array.isArray(value) ? value : [];
  };
  const features = asArray('product.features');
  const problemStats = asArray('problem.stats');
  const aboutStats = asArray('about.stats');
  const aboutPhotoAlts = asArray('about.photos');
  const partners = asArray('partners.list');
  const faqItems = asArray('faq.items');

  return (
    <div className="min-h-screen overflow-x-hidden bg-white font-sans text-slate-900 antialiased">
      <a href="#main" className="skip-link">
        {t('a11y.skipToContent')}
      </a>

      {/* -------------------------------------------------------------- Header */}
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
          scrolled || mobileMenuOpen
            ? 'nav-glass border-b border-slate-200/80'
            : 'bg-transparent'
        }`}
      >
        <nav
          aria-label={t('a11y.primaryNav')}
          className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4"
        >
          <a
            href="#main"
            className="flex items-center gap-2.5 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-4"
          >
            <img src="/assets/logo.png" alt="" className="h-8 w-8" />
            <span className="font-heading text-lg font-bold tracking-tight text-slate-900">
              CareAscension
            </span>
          </a>

          <ul className="hidden items-center gap-9 md:flex">
            {NAV_ITEMS.map((item) => (
              <li key={item}>
                <a href={`#${item}`} className={navLinkClass}>
                  {t(`nav.${item}`)}
                </a>
              </li>
            ))}
          </ul>

          <div className="hidden items-center gap-4 md:flex">
            <a
              href={PILOT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={navLinkClass}
            >
              {t('nav.login')}
            </a>
            <a
              href="#contact"
              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-soft transition duration-200 hover:-translate-y-0.5 hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2"
            >
              {t('nav.cta')}
            </a>
          </div>

          <button
            type="button"
            onClick={() => setMobileMenuOpen((open) => !open)}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
            aria-label={mobileMenuOpen ? t('a11y.closeMenu') : t('a11y.openMenu')}
            className="rounded-md p-2 text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary md:hidden"
          >
            {mobileMenuOpen ? <X {...iconProps} size={22} /> : <Menu {...iconProps} size={22} />}
          </button>
        </nav>

        {mobileMenuOpen && (
          <div
            id="mobile-menu"
            className="border-t border-slate-200 bg-white px-6 py-4 md:hidden"
          >
            <ul className="flex flex-col gap-1">
              {NAV_ITEMS.map((item) => (
                <li key={item}>
                  <a
                    href={`#${item}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className="block rounded-md px-2 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    {t(`nav.${item}`)}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href={PILOT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block rounded-md px-2 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  {t('nav.login')}
                </a>
              </li>
            </ul>
            <a
              href="#contact"
              onClick={() => setMobileMenuOpen(false)}
              className="mt-3 inline-flex w-full items-center justify-center rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-medium text-white"
            >
              {t('nav.cta')}
            </a>
          </div>
        )}
      </header>

      <main id="main" className="scroll-mt-24">
        {/* ------------------------------------------------------------ Hero */}
        <section
          aria-labelledby="hero-heading"
          className="relative overflow-hidden px-6 pb-20 pt-36 md:pt-44"
        >
          <div className="pointer-events-none absolute inset-0 bg-grid" aria-hidden="true" />
          <div
            className="pointer-events-none absolute -top-24 left-1/2 h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-blue-100/60 blur-3xl motion-safe:animate-float"
            aria-hidden="true"
          />

          <Reveal className="reveal-stagger relative mx-auto max-w-3xl text-center">
            <p className="mb-6 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-4 py-1.5 text-xs font-medium text-slate-600 shadow-soft">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
              {t('hero.badge')}
            </p>

            <h1
              id="hero-heading"
              className="font-heading text-4xl font-bold leading-[1.08] tracking-tight text-slate-900 sm:text-5xl md:text-6xl"
            >
              {t('hero.titleLead')}{' '}
              <span className="text-gradient">{t('hero.titleEmphasis')}</span>
            </h1>

            <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-slate-600">
              {t('hero.description')}
            </p>

            <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href="#contact"
                className="group inline-flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 px-6 py-3 text-sm font-semibold text-white shadow-lift transition duration-200 hover:-translate-y-0.5 hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 sm:w-auto"
              >
                {t('hero.ctaPrimary')}
                <ArrowRight
                  {...iconProps}
                  size={16}
                  className="transition-transform duration-200 group-hover:translate-x-0.5"
                />
              </a>
              <a
                href="#product"
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-700 transition duration-200 hover:-translate-y-0.5 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 sm:w-auto"
              >
                {t('hero.ctaSecondary')}
              </a>
            </div>
          </Reveal>

          <Reveal className="reveal relative mx-auto mt-16 max-w-5xl" rootMargin="0px">
            <div
              className="absolute -inset-x-6 -top-8 bottom-0 rounded-[2rem] bg-gradient-to-b from-blue-100/70 to-transparent blur-2xl"
              aria-hidden="true"
            />
            <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-2 shadow-lift">
              <img
                src="/assets/dashboard-screenshot.png"
                alt={t('hero.dashboardAlt')}
                width="1600"
                height="1000"
                className="w-full rounded-xl"
              />
            </div>
          </Reveal>
        </section>

        {/* --------------------------------------------------------- Problem */}
        <Section
          id="problem"
          labelledBy="problem-heading"
          className="border-t border-slate-100 bg-slate-50/60"
        >
          <Reveal className="reveal">
            <SectionIntro
              headingId="problem-heading"
              eyebrow={t('problem.eyebrow')}
              heading={t('problem.heading')}
            >
              <Trans
                i18nKey="problem.body"
                components={{ 1: <strong className="font-semibold text-slate-900" /> }}
              />
            </SectionIntro>
          </Reveal>

          <Reveal as="ul" className="reveal-stagger mt-12 grid gap-6 md:grid-cols-3">
            {problemStats.map((item) => (
              <li
                key={item.value}
                className="rounded-2xl border border-slate-200 bg-white p-7 shadow-soft transition duration-200 hover:-translate-y-1 hover:shadow-lift"
              >
                <p className="font-heading text-4xl font-bold tracking-tight text-primary">
                  {item.value}
                </p>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.label}</p>
              </li>
            ))}
          </Reveal>

          <Reveal
            as="figure"
            className="reveal chart-figure mt-6 rounded-2xl border border-slate-200 bg-white p-8 shadow-soft"
          >
            <figcaption className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="font-heading text-lg font-bold text-slate-900">
                {t('problem.chart.title')}
              </span>
              <span className="text-sm text-slate-600">{t('problem.chart.note')}</span>
            </figcaption>

            <div className="mt-6">
              <svg
                viewBox="0 0 400 240"
                className="h-[280px] w-full"
                role="img"
                aria-labelledby="chart-title chart-summary"
              >
                <title id="chart-title">{t('problem.chart.title')}</title>
                <desc id="chart-summary">{t('problem.chart.summary')}</desc>

                <g aria-hidden="true">
                  {[200, 150, 100, 50].map((y) => (
                    <line
                      key={y}
                      x1="40"
                      y1={y}
                      x2="380"
                      y2={y}
                      stroke="#e2e8f0"
                      strokeWidth="1"
                      strokeDasharray={y === 200 ? '0' : '4'}
                    />
                  ))}
                  <path
                    className="chart-area"
                    d="M 80,140 L 180,110 L 280,65 L 380,50 L 380,200 L 80,200 Z"
                    fill="url(#areaGradient)"
                    opacity="0.18"
                  />
                  <path
                    className="chart-line"
                    pathLength="1"
                    d="M 80,140 L 180,110 L 280,65 L 380,50"
                    fill="none"
                    stroke="url(#lineGradient)"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  {CHART_POINTS.map((p, index) => (
                    <g key={p.year} className="group">
                      <circle
                        cx={p.x}
                        cy={p.y}
                        r="14"
                        fill="#2563EB"
                        opacity="0"
                        className="transition-opacity group-hover:opacity-10"
                      />
                      <circle
                        cx={p.x}
                        cy={p.y}
                        r="5"
                        fill="#2563EB"
                        stroke="#fff"
                        strokeWidth="2"
                        className={`chart-dot chart-dot-${index}`}
                      />
                      <text
                        x={p.x}
                        y="228"
                        textAnchor="middle"
                        className="fill-slate-600 text-[11px] font-medium"
                      >
                        {p.year}
                      </text>
                      <text
                        x={p.x}
                        y={p.y - 14}
                        textAnchor="middle"
                        className="fill-slate-900 text-[12px] font-bold opacity-0 transition-opacity group-hover:opacity-100"
                      >
                        {numberFormat.format(p.count)}
                      </text>
                    </g>
                  ))}
                </g>

                <defs>
                  <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#60A5FA" />
                    <stop offset="100%" stopColor="#2563EB" />
                  </linearGradient>
                  <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#2563EB" />
                    <stop offset="100%" stopColor="#2563EB" stopOpacity="0" />
                  </linearGradient>
                </defs>
              </svg>
            </div>

            {/* Same data as the chart, for assistive tech */}
            <table className="sr-only">
              <caption>{t('problem.chart.title')}</caption>
              <thead>
                <tr>
                  <th scope="col">{t('problem.chart.yearHeader')}</th>
                  <th scope="col">{t('problem.chart.countHeader')}</th>
                </tr>
              </thead>
              <tbody>
                {CHART_POINTS.map((p) => (
                  <tr key={p.year}>
                    <th scope="row">{p.year}</th>
                    <td>{numberFormat.format(p.count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Reveal>
        </Section>

        {/* --------------------------------------------------------- Product */}
        <Section id="product" labelledBy="product-heading">
          <Reveal className="reveal">
            <SectionIntro
              headingId="product-heading"
              eyebrow={t('product.eyebrow')}
              heading={t('product.heading')}
            >
              {t('product.body')}
            </SectionIntro>
          </Reveal>

          <Reveal as="ul" className="reveal-stagger mt-12 grid gap-6 md:grid-cols-3">
            {features.map((feature, index) => {
              const Icon = FEATURE_ICONS[index] ?? BedDouble;
              return (
                <li
                  key={feature.title}
                  className="rounded-2xl border border-slate-200 bg-white p-7 shadow-soft transition duration-200 hover:-translate-y-1 hover:shadow-lift"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Icon {...iconProps} size={20} />
                  </span>
                  <h3 className="mt-5 font-heading text-lg font-bold text-slate-900">
                    {feature.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{feature.body}</p>
                </li>
              );
            })}
          </Reveal>
        </Section>

        {/* --------------------------------------------------------- Mission */}
        <Section
          labelledBy="mission-heading"
          className="border-y border-slate-100 bg-slate-900 text-white"
        >
          <Reveal className="reveal mx-auto max-w-3xl text-center">
            <h2
              id="mission-heading"
              className="mb-4 text-sm font-semibold uppercase tracking-[0.14em] text-blue-300"
            >
              {t('mission.eyebrow')}
            </h2>
            <p className="font-heading text-2xl font-semibold leading-snug tracking-tight md:text-3xl">
              {t('mission.statement')}
            </p>
          </Reveal>
        </Section>

        {/* ----------------------------------------------------------- About */}
        <Section id="about" labelledBy="about-heading">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <Reveal className="reveal">
              <SectionIntro
                headingId="about-heading"
                eyebrow={t('about.eyebrow')}
                heading={t('about.heading')}
              />
              <p className="mt-5 text-lg leading-relaxed text-slate-600">{t('about.body1')}</p>
              <p className="mt-4 text-lg leading-relaxed text-slate-600">{t('about.body2')}</p>

              <dl className="mt-8 grid grid-cols-3 gap-6 border-t border-slate-200 pt-8">
                {aboutStats.map((item) => (
                  <div key={item.label}>
                    <dt className="font-heading text-2xl font-bold text-slate-900">
                      {item.value}
                    </dt>
                    <dd className="mt-1 text-sm text-slate-600">{item.label}</dd>
                  </div>
                ))}
              </dl>
            </Reveal>

            <Reveal className="reveal-stagger grid grid-cols-2 gap-4">
              {ABOUT_PHOTOS.map((photo, index) => (
                <Photo
                  key={photo.src}
                  src={photo.src}
                  alt={aboutPhotoAlts[index] ?? ''}
                  className={photo.className}
                />
              ))}
            </Reveal>
          </div>
        </Section>

        {/* -------------------------------------------------------- Partners */}
        <Section
          id="partners"
          labelledBy="partners-heading"
          className="border-t border-slate-100 bg-slate-50/60"
        >
          <Reveal className="reveal">
            <SectionIntro
              headingId="partners-heading"
              eyebrow={t('partners.eyebrow')}
              heading={t('partners.heading')}
            >
              {t('partners.body')}
            </SectionIntro>
          </Reveal>

          <Reveal as="ul" className="reveal-stagger mt-12 grid gap-6 md:grid-cols-3">
            {partners.map((shelter) => (
              <li
                key={shelter.name}
                className="rounded-2xl border border-slate-200 bg-white p-7 shadow-soft transition duration-200 hover:-translate-y-1 hover:shadow-lift"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Building2 {...iconProps} size={20} />
                </span>
                <h3 className="mt-5 font-heading text-lg font-bold text-slate-900">
                  {shelter.name}
                </h3>
                <p className="mt-2 flex items-start gap-1.5 text-sm leading-relaxed text-slate-600">
                  <MapPin {...iconProps} size={15} className="mt-0.5 shrink-0 text-slate-500" />
                  {shelter.address}
                </p>
              </li>
            ))}
          </Reveal>
        </Section>

        {/* ------------------------------------------------------------- FAQ */}
        <Section id="faq" labelledBy="faq-heading">
          <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
            <Reveal className="reveal">
              <SectionIntro
                headingId="faq-heading"
                eyebrow={t('faq.eyebrow')}
                heading={t('faq.heading')}
              >
                <Trans
                  i18nKey="faq.footnote"
                  components={{
                    1: (
                      <a
                        href="#contact"
                        className="font-medium text-primary underline-offset-2 hover:underline"
                      >
                        link
                      </a>
                    ),
                  }}
                />
              </SectionIntro>
            </Reveal>

            <Reveal
              as="div"
              className="reveal-stagger divide-y divide-slate-200 border-y border-slate-200"
            >
              {faqItems.map((item) => (
                <FaqItem key={item.q} question={item.q} answer={item.a} />
              ))}
            </Reveal>
          </div>
        </Section>

        {/* --------------------------------------------------------- Contact */}
        <Section
          id="contact"
          labelledBy="contact-heading"
          className="border-t border-slate-100 bg-slate-50/60"
        >
          <div className="grid gap-12 lg:grid-cols-2 lg:items-start">
            <Reveal className="reveal">
              <SectionIntro
                headingId="contact-heading"
                eyebrow={t('contact.eyebrow')}
                heading={t('contact.heading')}
              >
                {t('contact.body')}
              </SectionIntro>
              <ul className="mt-8 space-y-3 text-sm">
                <li>
                  <a
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="inline-flex items-center gap-3 rounded-md text-slate-600 transition-colors hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                  >
                    <Mail {...iconProps} size={18} className="text-slate-500" />
                    {CONTACT_EMAIL}
                  </a>
                </li>
                <li className="flex items-center gap-3 text-slate-600">
                  <MapPin {...iconProps} size={18} className="text-slate-500" />
                  {t('contact.location')}
                </li>
              </ul>
            </Reveal>

            <Reveal className="reveal">
              <ContactForm />
            </Reveal>
          </div>
        </Section>
      </main>

      {/* ---------------------------------------------------------- Footer */}
      <footer className="border-t border-slate-200 px-6 py-12">
        <Reveal className="reveal mx-auto flex max-w-6xl flex-col items-center justify-between gap-6 md:flex-row">
          <div className="flex items-center gap-2.5">
            <img src="/assets/logo.png" alt="" className="h-7 w-7" />
            <span className="font-heading font-bold text-slate-900">CareAscension</span>
          </div>

          <p className="text-center text-sm text-slate-600">
            {t('footer.tagline')} · {t('footer.rights', { year: new Date().getFullYear() })}
          </p>

          <ul className="flex items-center gap-5">
            <li>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('footer.instagram')}
                className="block rounded-md text-slate-500 transition-colors hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                <Instagram {...iconProps} size={20} />
              </a>
            </li>
            <li>
              <a
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('footer.linkedin')}
                className="block rounded-md text-slate-500 transition-colors hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                <Linkedin {...iconProps} size={20} />
              </a>
            </li>
            <li>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                aria-label={t('footer.email')}
                className="block rounded-md text-slate-500 transition-colors hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                <Mail {...iconProps} size={20} />
              </a>
            </li>
          </ul>
        </Reveal>
      </footer>
    </div>
  );
}
